import { Injectable } from '@angular/core';
import { CommonManage } from '@app/administration/process-automation/shared/common-manage';
import {
ActividadBase,
Actividad,
Option,
Tarea
} from '@app/shared';
import { StorageService, FlowOrigin } from './storage.service';
import { sortBy, merge, findIndex } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Logger } from '@app/core';

import { environment } from '@env/environment';
import { ActivityService } from '@app/shared/activity/shared/activity.service';
import { HelperFlowService } from '@app/shared/services/helper-flow.service';
import { ConfigFields } from '@app/shared/activity/shared/form-service';


const log = new Logger('ManageActivityService');

/**
 * Administra todo lo relacionado con las actividades,
 * en la automatización de procesos.
 *
 * @export
 * @class ManageActivityService
 * @extends {CommonManage}
 */
@Injectable()
export class ManageActivityService extends CommonManage {
  // Estados de las actividades
  status: ActividadBase[];
  // Todas las tareas de las actividades del flujo
  allTasks: Tarea[] = [];
  // Copia de las 'actividades' del flujo consultado
  activities: Actividad[];

  constructor(
    protected ss: StorageService,
    private as: ActivityService,
    private hfs: HelperFlowService,
    private toastr: ToastrService
  ) {
    super();
    // Consultar las actividades del flujo actual
    this.ss.getActivities().subscribe(data => {
      this.activities = data;
      let alltasks: Tarea[] = [];
      data.map(a => {
        alltasks = [...alltasks, ...a.tareas];
      });
      this.allTasks = alltasks;
    });
    // Consultar los posibles estado de una actividad
    this.as.getStatusActivity(false).then(data => {
      this.status = data;
    });
  }

  /**
   * Retorna las actividades refinadas(con metadatos).
   *
   * @returns
   * @memberof ManageActivityService
   */
  getRefineActivities() {
    return this.hfs.refineActivities(this.activities);
  }

  /**
   * Renorna una lista de opciones compatibles para un select
   * generado por un formulario dinámico, para almacenar los estados
   * de las actividades.
   *
   * @returns {Option[]}
   * @memberof ManageActivityService
   */
  getStatusOptions(): Option[] {
    return this.status.map(item => {
      return { key: item.id, value: item.descripcion };
    });
  }

  /**
   * Retorna los controles necesarios para crear un formulario dinámico.
   *
   * @param {Partial<ConfigFields>} config
   * @returns
   * @memberof ManageActivityService
   */
  getControlsForDynamicForm(config: Partial<ConfigFields>) {
    return this.as.getControls(config);
  }

  /**
   * Crear una nueva actividad en el flujo, y sera ordenada
   * en la última posición del array o en un orden específico.
   *
   * @param {Partial<Actividad>} data
   * @param {number} [nextOrder=0]
   * @memberof ManageActivityService
   */
  new(data: Partial<Actividad>, nextOrder: number = 0) {
    const activities = this.getRefineActivities();
    // Determinar el próximo orden de la actividad
    const order = nextOrder ? nextOrder : this.getNextOrder(activities);
    // Crear formato de la actividad
    const activity = merge(
      data,
      new Actividad({
        id: this.ss.getNextId('activity'),
        orden: order,
        tarea_padre: this.ss.currentFlowType === 'microproces' ? this.ss.flow.id : null
      })
    );
    // Validar si se reordenan las actividades
    const currentOrder = this.getCurrentOrder(activities);
    // Si el proximo orden no es mayor al actual,
    // es porque se desea crear una actividad con un orden específico
    if (nextOrder && !(order > currentOrder)) {
      const activitiesReOrder = this.updateOrder(activities, order);
      // Actualizar orden de las actividades
      // Dejar disponible el espacio para la nueva actividad
      this.activities = this.hfs.refineActivities(activitiesReOrder);
      // Si son actividades ya sincronizadas en DB se agregan al historial de actualizaciones
      this.registerOrderUpdateInHistory('activities', this.activities);
    }
    // Agregar nueva actividad
    this.add(activity);
    // Aplicar id usado para crear la actividad
    // Prepara el contador global para la próxima creación.
    this.ss.applyNextId('activity');
    // Notificar al usuario.
    if (environment.showDebugHelpers) {
      this.toastr.success(`Actividad creada correctamente con el id: ${activity.id}.`, 'Operación exitosa');
    } else {
      this.toastr.success(`Actividad creada correctamente`, 'Operación exitosa');
    }
  }

  /**
   * Agregar una nueva actividad en el flujo en
   * memoria y actualiza la referencia en LocalStorage.
   *
   * @param {Actividad} activity
   * @memberof ManageActivityService
   */
  add(activity: Actividad) {
    // Guardar actividad en el flujo
    this.activities.push(activity);
    // Si el flujo esta sincronizado con la DB entonces se guarda
    // esta acción en un historial.
    this.ss.registerActionInHistory('activities', 'create', {
      activityId: activity.id
    });
    // Ordenar actividades en forma asc en base a su key "orden"
    // Agregar metadatos
    this.activities = this.getRefineActivities();
    // this._activities = sortBy(this._activities, 'orden');
    // Guardar cambios en LocalStorage
    this.saveInLocal();
  }

  /**
   * Actualizar la información de una actividad existente.
   *
   * @param {Actividad} activity
   * @param {boolean} [skipRegisterAction=false]
   * @returns {boolean}
   * @memberof ManageActivityService
   */
  edit(activity: Actividad, skipRegisterAction: boolean = false): boolean {
    const indexOldActivity = findIndex(this.activities, { id: Number(activity.id) });
    if (indexOldActivity !== -1) {
      // Actualizar actividad en memoria
      this.activities[indexOldActivity] = activity;
      // Si el flujo esta sincronizado con la DB entonces se guarda
      // esta acción en un historial.
      if (!skipRegisterAction) {
        this.ss.registerActionInHistory('activities', 'update', {
          activityId: activity.id,
        }, activity.id);
      }
      // Guardar cambios en LocalStorage
      this.saveInLocal();
      return true;
    }
    return false;
  }

  /**
   * Remover una actividad del flujo.
   *
   * @param {Actividad} activity
   * @param {FlowOrigin} origing
   * @returns {boolean}
   * @memberof ManageActivityService
   */
  remove(activity: Actividad, origing: FlowOrigin): boolean {
    const indexOldTask = findIndex(this.activities, {id: activity.id});
    const removed = this.removeObject(this.activities, activity.id);
    // Si la actividad fue removida
    if (removed) {
      // Ordenar los elementos en base a su posición en el array
      this.reorderWithIndex(this.activities);
      if (this.activities && this.activities.length) {
        this.activities.forEach((act, index) => {
          if (index >= indexOldTask) {
            this.edit(act);
          }
        });
      }
      // Si no hay elementos en el array se reinicia el consecutivo a 0
      if (!this.activities.length) {
        this.ss.resetId('activity');
        this.ss.resetId('task');
        this.ss.resetId('option');
      } else {
        // Actualizar consecutivos
        this.ss.updateAllConsecutive(origing);
      }
      // Si el flujo esta sincronizado con la DB entonces se guarda
      // esta acción en un historial.
      this.ss.registerActionInHistory('activities', 'delete', {
        activityId: activity.id
      }, activity.id);
      // Guardar cambios en LocalStorage
      this.saveInLocal();
      // Notificar al usuario
      if (environment.showDebugHelpers) {
        this.toastr.success(
          `La actividad con el id: ${activity.id} fue eliminada correctamente.`, 'Operación exitosa'
        );
      } else {
        this.toastr.success(`La actividad fue eliminada correctamente.`, 'Operación exitosa');
      }
      return true;
    } else {
      if (environment.showDebugHelpers) {
        this.toastr.error(`La actividad con el id: ${activity.id} No se pudó eliminar correctamente.`, 'Error');
      } else {
        this.toastr.error(`La actividad No se pudó eliminar correctamente.`, 'Error');
      }
      return false;
    }
  }

  /**
   *  Autoriza el almacenamiento de las actividades en LocalStorage.
   *
   * @private
   * @memberof ManageActivityService
   */
  saveInLocal() {
    this.ss.setActivities(this.activities);
  }
}



// WEBPACK FOOTER //
// ./src/app/administration/process-automation/shared/manage-activity.service.ts